var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Label, Text, Title } from "../../tremor/Text";
import { Button } from "../../tremor/Button";
import { Icon } from "../../tremor/Icon";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { TrashIcon, ArrowRightIcon, XMarkIcon, } from "@heroicons/react/16/solid";
import { toast } from "sonner";
import React, { memo, useEffect, useMemo, useState } from "react";
import { useBackend } from "../../layouts/Wrapper";
import { useDashboard } from "../../layouts/Dashboard/useDashboard";
import { twMerge } from "tailwind-merge";
dayjs.extend(relativeTime);
function groupTextsByTime(entries) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    const groups = {
        Today: [],
        'Previous 7 Days': [],
        'Previous 30 Days': [],
    };
    // Create month buckets for months until January of current year
    const months = {};
    // Create year buckets for previous years
    const years = {};
    for (let i = 0; i <= now.getMonth(); i++) {
        const monthDate = new Date(today);
        monthDate.setMonth(today.getMonth() - i);
        const monthName = monthDate.toLocaleString('default', { month: 'long' });
        months[monthName] = [];
    }
    entries.forEach(entry => {
        const entryDate = new Date(entry.created_at);
        const entryDay = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
        // Check for today
        if (entryDay.getTime() === today.getTime()) {
            groups['Today'].push(entry);
        }
        // Check for previous 7 days
        else if (entryDay >= sevenDaysAgo && entryDay < today) {
            groups['Previous 7 Days'].push(entry);
        }
        // Check for previous 30 days
        else if (entryDay >= thirtyDaysAgo && entryDay < sevenDaysAgo) {
            groups['Previous 30 Days'].push(entry);
        }
        // Check if it's from current year and before current month
        else if (entryDate.getFullYear() === now.getFullYear() && entryDate.getMonth() <= now.getMonth()) {
            const monthName = entryDate.toLocaleString('default', { month: 'long' });
            if (months[monthName]) {
                months[monthName].push(entry);
            }
        }
        // Group by year for older entries
        else {
            const year = entryDate.getFullYear().toString();
            if (!years[year]) {
                years[year] = [];
            }
            years[year].push(entry);
        }
    });
    // Sort years in descending order
    const sortedYears = Object.keys(years)
        .sort((a, b) => parseInt(b) - parseInt(a))
        .reduce((obj, year) => {
        obj[year] = years[year];
        return obj;
    }, {});
    // Merge all groups
    return {
        groups,
        months,
        years: sortedYears
    };
}
const QuestionSidebarCard = ({ question, onDelete, onClick, expanded }) => {
    let truncated = expanded
        ? question.query
        : question.query.slice(0, 100) + "...";
    return (React.createElement("div", { className: "onvo-transition-all onvo-group onvo-relative onvo-flex onvo-w-full onvo-px-4 onvo-py-3 hover:onvo-bg-black/10 hover:onvo-dark:bg-white/10 onvo-rounded-2xl onvo-border-solid onvo-border onvo-border-black/10 dark:onvo-border-white/10" },
        React.createElement("div", { className: "onvo-flex-grow onvo-flex onvo-flex-col onvo-justify-between onvo-cursor-pointer", onClick: onClick },
            React.createElement(Text, { className: "onvo-text-md onvo-mb-2" }, truncated),
            React.createElement(Label, { className: "onvo-font-semibold onvo-text-xs" }, dayjs(question.created_at).fromNow())),
        onDelete && (React.createElement(Icon, { variant: "shadow", size: "xs", className: "onvo-absolute onvo-background-color onvo-border-black/10 dark:onvo-border-white/10 onvo-text-red-500 onvo-right-2 onvo-top-2 onvo-cursor-pointer onvo-opacity-0 group-hover:onvo-opacity-100", icon: TrashIcon, onClick: onDelete }))));
};
const QuestionHistoryRaw = ({ onSelect, onDelete, onExpanded, variant, onNew, selectedId }) => {
    const { backend } = useBackend();
    const { dashboard } = useDashboard();
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [questions, setQuestions] = useState([]);
    const getQuestions = () => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        if (!backend || !dashboard)
            return;
        let qs = yield backend.questions.list({ dashboard: dashboard.id });
        let sorted = qs.sort((a, b) => {
            return (new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        });
        setQuestions(sorted);
        setLoading(false);
    });
    useEffect(() => {
        if (dashboard) {
            getQuestions();
        }
    }, [dashboard]);
    const deleteQuestion = (questionId) => {
        if (!backend)
            return;
        toast.promise(backend.questions.delete(questionId), {
            loading: "Deleting question...",
            success: () => {
                if (onDelete) {
                    onDelete();
                }
                getQuestions();
                return "Question deleted";
            },
            error: (e) => {
                return "Failed to delete question: " + e.message;
            },
        });
    };
    const filteredQuestions = useMemo(() => {
        if (expanded)
            return questions;
        else
            return questions.slice(0, 6);
    }, [expanded, questions]);
    if (!loading && filteredQuestions.length === 0) {
        return React.createElement(React.Fragment, null);
    }
    if (variant === "sidebar") {
        let { groups, months, years } = groupTextsByTime(questions.map(a => ({
            id: a.id,
            created_at: a.created_at,
            query: a.query,
        })));
        const layout = (question) => {
            return React.createElement("div", { onClick: () => onSelect(questions.find(a => a.id === question.id)), className: "onvo-rounded-md onvo-relative onvo-group onvo-px-2 onvo-py-2.5 onvo-cursor-pointer hover:onvo-bg-black/5 dark:hover:onvo-bg-white/5 " + (selectedId === question.id ? "onvo-bg-black/10 dark:onvo-bg-white/10" : ""), key: question.id },
                React.createElement(Text, { className: "onvo-whitespace-nowrap onvo-overflow-hidden onvo-text-ellipsis" }, question.query),
                onDelete && (React.createElement(Icon, { variant: "shadow", size: "xs", className: "onvo-absolute onvo-background-color onvo-border-black/10 dark:onvo-border-white/10 onvo-text-red-500 onvo-right-[6px] onvo-top-[6px] onvo-cursor-pointer onvo-opacity-0 group-hover:onvo-opacity-100", icon: TrashIcon, onClick: () => deleteQuestion(question.id) })));
        };
        return React.createElement("div", { className: "onvo-w-full onvo-flex-shrink-0 onvo-overflow-auto onvo-background-color onvo-h-full onvo-scrollbar-thin" },
            React.createElement(Button, { variant: "primary", className: "onvo-w-[calc(100%-16px)] onvo-m-2", onClick: () => {
                    if (onNew) {
                        onNew();
                    }
                } }, "+ New widget"),
            Object.keys(groups).filter(group => groups[group].length > 0).map((group) => {
                return React.createElement("div", { key: group, className: "onvo-p-2" },
                    React.createElement(Text, { className: "onvo-font-semibold onvo-text-xs onvo-p-1" }, group),
                    groups[group].map(layout));
            }),
            Object.keys(months).filter(month => months[month].length > 0).map((month) => {
                return React.createElement("div", { key: month, className: "onvo-p-2" },
                    React.createElement(Text, { className: "onvo-font-semibold onvo-text-xs onvo-p-1" }, month),
                    months[month].map(layout));
            }),
            Object.keys(years).map((year) => {
                return React.createElement("div", { key: year, className: "onvo-p-2" },
                    React.createElement(Text, { className: "onvo-font-semibold onvo-text-xs onvo-p-1" }, year),
                    years[year].map(layout));
            }));
    }
    return (React.createElement("div", { className: "onvo-w-full onvo-flex onvo-flex-col " },
        React.createElement("div", { className: "onvo-flex onvo-w-full onvo-p-2 onvo-flex-row onvo-items-center onvo-justify-between" },
            React.createElement(Title, null, "History"),
            React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-items-center" }, expanded ? (React.createElement(Button, { variant: "ghost", onClick: () => {
                    setExpanded(false);
                    onExpanded(false);
                } },
                "View less",
                React.createElement(XMarkIcon, { className: "onvo-size-4 onvo-ml-1" }))) : (React.createElement(Button, { variant: "ghost", onClick: () => {
                    setExpanded(true);
                    onExpanded(true);
                } },
                "View more",
                React.createElement(ArrowRightIcon, { className: "onvo-size-4 onvo-ml-1" }))))),
        React.createElement("div", { className: twMerge("onvo-w-full onvo-grid onvo-gap-3", expanded
                ? "onvo-grid-cols-1"
                : "onvo-grid-cols-2 @xl/questionmodal:onvo-grid-cols-3") },
            loading &&
                [1, 2, 3, 4, 5, 6].map((a) => (React.createElement("div", { key: "question-history-loader-" + a, className: "onvo-transition-all onvo-group onvo-relative onvo-flex onvo-flex-col onvo-w-full onvo-px-3 onvo-py-2 onvo-rounded-lg onvo-border-solid onvo-border onvo-border-black/10 dark:onvo-border-white/10" },
                    React.createElement("div", { className: "onvo-h-6 onvo-bg-slate-200 onvo-rounded-md dark:onvo-bg-slate-700 onvo-w-[95%] onvo-mb-1" }),
                    React.createElement("div", { className: "onvo-w-[30%] onvo-h-4 onvo-mb-2 onvo-bg-slate-200 onvo-rounded-md dark:onvo-bg-slate-700" }),
                    React.createElement("div", { className: "onvo-w-[60%] onvo-h-3 onvo-bg-slate-200 onvo-rounded-md dark:onvo-bg-slate-700" })))),
            filteredQuestions.map((a) => (React.createElement(QuestionSidebarCard, { onDelete: () => deleteQuestion(a.id), question: a, key: a.id, onClick: () => onSelect(a), expanded: expanded }))))));
};
export const QuestionHistory = memo(QuestionHistoryRaw);
//# sourceMappingURL=QuestionHistory.js.map